<template>
  <van-popup
    :show="show"
    position="bottom"
    :style="{ height: '75%' }"
    round
    closeable
    @update:show="updateShow"
  >
    <div class="search-popup">
      <div class="popup-header">
        <h3>关联到{{ sessionType === 'group_chat' || sessionType === 'group_chat_tools' ? '群聊' : '客户' }}</h3>
      </div>
      <van-tabs v-if="!isGroupChat" v-model:active="activeTab" class="popup-tabs" sticky offset-top="0">
        <van-tab title="学员" name="student">
          <div class="tab-content">
            <van-search
              v-model="studentKeyword"
              placeholder="搜索学员姓名或手机号"
              shape="round"
              @update:model-value="onStudentSearch"
            />
            <div v-if="studentLoading" class="loading-block"><van-loading type="spinner" /></div>
            <div v-else-if="studentResults.length === 0" class="empty-block">暂无匹配学员</div>
            <div v-else class="result-list">
              <div
                v-for="item in studentResults"
                :key="`stu-${item.targetId}`"
                class="result-item"
                @click="handleBindStudent(item)"
              >
                <div class="result-avatar">{{ (item.name || '-').slice(0, 1) }}</div>
                <div class="result-info">
                  <div class="result-name-row">
                    <span class="result-name">{{ item.name || '-' }}</span>
                    <span class="result-tag student-tag">学员</span>
                  </div>
                  <div class="result-meta">
                    <span v-if="item.grade" class="meta-item">{{ getGradeLabel(item.grade) }}</span>
                    <span v-if="item.schoolName" class="meta-item">{{ item.schoolName }}</span>
                    <span v-if="item.ownerName" class="meta-item">{{ item.ownerName }}</span>
                  </div>
                </div>
                <van-icon name="arrow" color="#c0c4cc" />
              </div>
            </div>
          </div>
        </van-tab>
        <van-tab title="线索" name="consultation">
          <div class="tab-content">
            <van-search
              v-model="consultationKeyword"
              placeholder="搜索线索联系人或手机号"
              shape="round"
              @update:model-value="onConsultationSearch"
            />
            <div v-if="consultationLoading" class="loading-block"><van-loading type="spinner" /></div>
            <div v-else-if="consultationResults.length === 0" class="empty-block">暂无匹配线索</div>
            <div v-else class="result-list">
              <div
                v-for="item in consultationResults"
                :key="`con-${item.targetId}`"
                class="result-item"
                @click="handleBindConsultation(item)"
              >
                <div class="result-avatar consultation-avatar">{{ (item.name || '-').slice(0, 1) }}</div>
                <div class="result-info">
                  <div class="result-name-row">
                    <span class="result-name">{{ item.name || '-' }}</span>
                    <span class="result-tag consultation-tag">线索</span>
                  </div>
                  <div class="result-meta">
                    <span v-if="item.phone || item.otherConcatMethods" class="meta-item">{{ item.phone || item.otherConcatMethods }}</span>
                    <span v-if="getGradeLabel(item.grade)" class="meta-item">{{ getGradeLabel(item.grade) }}</span>
                    <span v-if="item.ownerName" class="meta-item">{{ item.ownerName }}</span>
                  </div>
                </div>
                <van-icon name="arrow" color="#c0c4cc" />
              </div>
            </div>
          </div>
        </van-tab>
      </van-tabs>
      <div v-else class="popup-single-tab">
        <div class="tab-content">
          <van-search
            v-model="studentKeyword"
            placeholder="搜索学员姓名或手机号"
            shape="round"
            @update:model-value="onStudentSearch"
          />
          <div v-if="studentLoading" class="loading-block"><van-loading type="spinner" /></div>
          <div v-else-if="studentResults.length === 0" class="empty-block">暂无匹配学员</div>
          <div v-else class="result-list">
            <div
              v-for="item in studentResults"
              :key="`stu-${item.targetId}`"
              class="result-item"
              @click="handleBindStudent(item)"
            >
              <div class="result-avatar">{{ (item.name || '-').slice(0, 1) }}</div>
              <div class="result-info">
                <div class="result-name-row">
                  <span class="result-name">{{ item.name || '-' }}</span>
                  <span class="result-tag student-tag">学员</span>
                </div>
                <div class="result-meta">
                  <span v-if="item.grade" class="meta-item">{{ getGradeLabel(item.grade) }}</span>
                  <span v-if="item.schoolName" class="meta-item">{{ item.schoolName }}</span>
                  <span v-if="item.ownerName" class="meta-item">{{ item.ownerName }}</span>
                </div>
              </div>
              <van-icon name="arrow" color="#c0c4cc" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { searchBindingStudents, searchBindingConsultations, bindStudent, bindConsultation } from '@/api/crm'
import { useDict } from '@/utils/dict'

const props = defineProps({
  show: { type: Boolean, default: false },
  sessionType: { type: String, default: '' },
  sessionId: { type: String, default: '' },
  initialTab: { type: String, default: 'student' }
})

const isGroupChat = computed(() => props.sessionType === 'group_chat' || props.sessionType === 'group_chat_tools')

const emit = defineEmits(['update:show', 'bound'])

const { grade_list } = useDict('grade_list')

const activeTab = ref('student')
const studentKeyword = ref('')
const consultationKeyword = ref('')
const studentResults = ref([])
const consultationResults = ref([])
const studentLoading = ref(false)
const consultationLoading = ref(false)
let studentSearchTimer = null
let consultationSearchTimer = null

watch(() => props.show, val => {
  if (val) {
    activeTab.value = props.initialTab || 'student'
    studentKeyword.value = ''
    consultationKeyword.value = ''
    studentResults.value = []
    consultationResults.value = []
    setTimeout(() => {
      triggerSearchForActiveTab()
    }, 100)
  }
})

watch(() => props.initialTab, val => {
  if (props.show && val) {
    activeTab.value = val
    triggerSearchForActiveTab()
  }
})

watch(activeTab, () => {
  if (props.show) {
    triggerSearchForActiveTab()
  }
})

function triggerSearchForActiveTab() {
  if (isGroupChat.value) {
    if (studentResults.value.length === 0) {
      onStudentSearch()
    }
  } else if (activeTab.value === 'consultation') {
    if (consultationResults.value.length === 0) {
      onConsultationSearch()
    }
  } else {
    if (studentResults.value.length === 0) {
      onStudentSearch()
    }
  }
}

function updateShow(val) {
  emit('update:show', val)
}

function getGradeLabel(grade) {
  if (grade === undefined || grade === null || grade === '') return ''
  const list = grade_list.value || []
  const matched = list.find(item => String(item.dictValue) === String(grade))
  return matched ? matched.dictLabel : ''
}

function onStudentSearch() {
  if (studentSearchTimer) clearTimeout(studentSearchTimer)
  const keyword = studentKeyword.value.trim()
  studentSearchTimer = setTimeout(async () => {
    studentLoading.value = true
    try {
      studentResults.value = await searchBindingStudents({ keyword }) || []
    } catch (e) {
      showToast(e?.message || '搜索学员失败')
    } finally {
      studentLoading.value = false
    }
  }, 300)
}

function onConsultationSearch() {
  if (consultationSearchTimer) clearTimeout(consultationSearchTimer)
  const keyword = consultationKeyword.value.trim()
  consultationSearchTimer = setTimeout(async () => {
    consultationLoading.value = true
    try {
      consultationResults.value = await searchBindingConsultations({ keyword }) || []
    } catch (e) {
      showToast(e?.message || '搜索线索失败')
    } finally {
      consultationLoading.value = false
    }
  }, 300)
}

async function handleBindStudent(item) {
  try {
    showLoadingToast({ message: '绑定中...', forbidClick: true })
    await bindStudent(props.sessionType, props.sessionId, item.targetId)
    closeToast()
    showToast('关联成功')
    emit('bound')
    updateShow(false)
  } catch (e) {
    closeToast()
    showToast(e?.message || '绑定失败')
  }
}

async function handleBindConsultation(item) {
  try {
    showLoadingToast({ message: '绑定中...', forbidClick: true })
    await bindConsultation(props.sessionType, props.sessionId, item.targetId)
    closeToast()
    showToast('关联成功')
    emit('bound')
    updateShow(false)
  } catch (e) {
    closeToast()
    showToast(e?.message || '绑定失败')
  }
}
</script>

<style scoped>
.search-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f7f8fc;
}
.popup-header {
  padding: 16px;
  background: #fff;
  text-align: center;
}
.popup-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}
.popup-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.popup-tabs :deep(.van-tabs__content) {
  flex: 1;
  overflow-y: auto;
}
.popup-single-tab {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f7f8fc;
}
.tab-content {
  padding-bottom: 20px;
}
.loading-block,
.empty-block,
.tip-block {
  padding: 40px 0;
  text-align: center;
  font-size: 13px;
}
.loading-block {
  color: #94a3b8;
}
.empty-block {
  color: #94a3b8;
}
.tip-block {
  color: #cbd5e1;
}
.result-list {
  padding: 0 12px;
}
.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 12px;
  margin-bottom: 8px;
}
.result-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
}
.result-avatar.consultation-avatar {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}
.result-info {
  flex: 1;
  min-width: 0;
}
.result-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.result-name {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}
.result-tag {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
}
.student-tag {
  background: #eff6ff;
  color: #2563eb;
}
.consultation-tag {
  background: #f3e8ff;
  color: #7c3aed;
}
.result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 12px;
  color: #64748b;
}
.meta-item:not(:last-child)::after {
  content: '·';
  margin-left: 10px;
  color: #cbd5e1;
}
</style>
